import { useState } from "react";

export default function BulkUploadModal({
    show,
    onClose,
    onSuccess
}) {
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [progress, setProgress] = useState({});
    const [error, setError] = useState(null);
    const [mediaType, setMediaType] = useState("image");
    const [tags, setTags] = useState("");

    if (!show) return null;

    const chooseFiles = (e) => {
        setFiles(Array.from(e.target.files));
        setProgress({});
        setError(null);
    };

    const uploadToImageKitApi = async (file, index) => {
        const baseUrl =
            process.env.REACT_APP_ENV === "local"
                ? process.env.REACT_APP_BACKEND_API_LOCAL
                : process.env.REACT_APP_BACKEND_API;

        const token =
            process.env.REACT_APP_ADMIN_MEDIA_TOKEN;

        const formData = new FormData();
        formData.append("image", file);

        const response = await fetch(
            `${baseUrl}/api/r2/upload`, // r2 cloudflare upload endpoint
            // `${baseUrl}/api/imagekit/upload`, //imagekit upload endpoint
            {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                body: formData,
            }
        );

        if (!response.ok) {
            throw new Error(`Failed to upload ${file.name}`);
        }

        const result = await response.json();

        const data = result.data || result;

        setProgress(prev => ({
            ...prev,
            [index]: 100
        }));

        // Parse tags string into array
        const tagsArray = tags
            .split(",")
            .map(tag => tag.trim())
            .filter(tag => tag.length > 0);

        return {
            name: data.name || file.name,
            fileName: data.name || file.name,
            url: data.url || data.imageUrl || data.fileUrl,
            type: mediaType,
            tags: tagsArray
        };
    };

    const uploadFiles = async () => {
        if (files.length === 0) return;

        setLoading(true);
        setError(null);

        try {
            const uploadedItems = await Promise.all(
                files.map((file, index) =>
                    uploadToImageKitApi(file, index)
                )
            );

            const apiBaseUrl =
                process.env.REACT_APP_ENV === "local"
                    ? process.env.REACT_APP_BACKEND_API_LOCAL
                    : process.env.REACT_APP_BACKEND_API;

            const submitResponse = await fetch(
                `${apiBaseUrl}/api/home/submit-media`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${process.env.REACT_APP_AUTH_TOKEN}`
                    },
                    body: JSON.stringify(uploadedItems)
                }
            );

            if (!submitResponse.ok) {
                throw new Error("Failed to save media");
            }

            alert("Bulk Upload Successful");

            onSuccess();

            setFiles([]);
            setProgress({});
            setTags("");
            setMediaType("image");
            onClose();

        } catch (err) {
            console.error(err);
            setError(err.message);
        }

        setLoading(false);
    };

    return (
        <div
            className="modal fade show d-block"
            style={{
                background: "rgba(0,0,0,.55)"
            }}
        >
            <div className="modal-dialog modal-lg modal-dialog-centered">
                <div className="modal-content">

                    <div className="modal-header">
                        <h5 className="modal-title">
                            Bulk Upload Media
                        </h5>

                        <button
                            className="btn-close"
                            onClick={onClose}
                        />
                    </div>

                    <div className="modal-body">

                        {error && (
                            <div className="alert alert-danger">
                                {error}
                            </div>
                        )}

                        <div className="row mb-3">
                            <div className="col-md-6">
                                <label className="form-label">
                                    Media Type <span className="text-danger">*</span>
                                </label>
                                <select
                                    className="form-select"
                                    value={mediaType}
                                    onChange={(e) => setMediaType(e.target.value)}
                                >
                                    <option value="image">Image</option>
                                    <option value="video">Video</option>
                                </select>
                            </div>
                            <div className="col-md-6">
                                <label className="form-label">
                                    Tags (Optional)
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Enter tags separated by commas (e.g. nature, sunset, landscape)"
                                    value={tags}
                                    onChange={(e) => setTags(e.target.value)}
                                />
                                <small className="text-muted">
                                    Separate tags with commas
                                </small>
                            </div>
                        </div>

                        <div className="mb-3">
                            <label className="form-label">
                                Select Files <span className="text-danger">*</span>
                            </label>
                            <input
                                type="file"
                                multiple
                                accept="image/*,video/*"
                                className="form-control"
                                onChange={chooseFiles}
                            />
                        </div>

                        <hr />

                        {files.length === 0 && (
                            <div className="text-center text-muted">
                                No files selected
                            </div>
                        )}

                        {files.map((file, index) => (
                            <div
                                key={index}
                                className="border rounded p-3 mb-3 d-flex align-items-center justify-content-between"
                            >
                                <div>
                                    <strong>
                                        {file.name}
                                    </strong>
                                    <div className="text-muted">
                                        {file.type}
                                    </div>
                                    <small>
                                        {(file.size / 1024 / 1024).toFixed(2)} MB
                                    </small>
                                </div>

                                <div style={{ width: 180 }}>
                                    <div className="progress">
                                        <div
                                            className="progress-bar progress-bar-striped progress-bar-animated"
                                            style={{
                                                width: `${progress[index] || 0}%`
                                            }}
                                        >
                                            {progress[index] || 0}%
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}

                    </div>

                    <div className="modal-footer">
                        <button
                            className="btn btn-secondary"
                            onClick={onClose}
                            disabled={loading}
                        >
                            Cancel
                        </button>

                        <button
                            className="btn btn-primary"
                            disabled={loading || files.length === 0}
                            onClick={uploadFiles}
                        >
                            {loading
                                ? "Uploading..."
                                : `Upload ${files.length} Files`}
                        </button>
                    </div>

                </div>
            </div>
        </div>
    );
}